/**
 * InboxPage — unified Client Inbox at /inbox
 *
 * Combines the two previously-separate inboxes into one tabbed surface:
 *  - "All"     → chronological merged stream of both sources (with badge)
 *  - "Email"   → just email replies (Resend inbound) — wraps <ClientReplies/>
 *  - "Portal"  → just portal messages (firm-side) — wraps <MessagesPage/>
 *
 * Why a wrapper instead of a full merge: the existing pages already work
 * great in isolation. The wrapper gives users one nav entry + a unified
 * "All" stream that adds value, without rewriting two production surfaces.
 *
 * Backwards-compat: /client-replies and /messages still route to this page
 * with the appropriate tab pre-selected.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Inbox, Mail, MessageCircle, ExternalLink, Loader2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import ClientReplies from './ClientReplies';
import MessagesPage from './MessagesPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

function AllStream() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!wsId) return;
    (async () => {
      setLoading(true);
      try {
        const [emailRes, portalRes] = await Promise.all([
          apiFetch(`${API_URL}/api/inbound/messages?workspace_id=${wsId}&limit=50`).catch(() => null),
          apiFetch(`${API_URL}/api/client-portal/workspace-messages?workspace_id=${wsId}`).catch(() => null),
        ]);

        const emailItems = emailRes && emailRes.ok
          ? ((await emailRes.json())?.messages || []).map((m) => ({
              id: `email_${m.message_id || m.id}`,
              source: 'email',
              from: m.from_name || m.from_email || 'Unknown',
              subject: m.subject || '(no subject)',
              preview: (m.body_text || m.snippet || '').slice(0, 160),
              at: m.received_at || m.created_at,
              unread: !m.read_at,
              link: '/client-replies',
            }))
          : [];

        const portalItems = portalRes && portalRes.ok
          ? ((await portalRes.json())?.threads || []).flatMap((t) => {
              const last = t.last_message || {};
              return [{
                id: `portal_${t.contact_id}`,
                source: 'portal',
                from: t.contact_name || 'Client',
                subject: '(portal message)',
                preview: (last.body || '').slice(0, 160),
                at: last.created_at || t.last_activity_at,
                unread: (t.unread_count || 0) > 0,
                link: `/messages?contact=${t.contact_id}`,
              }];
            })
          : [];

        const merged = [...emailItems, ...portalItems]
          .filter((i) => i.at)
          .sort((a, b) => new Date(b.at) - new Date(a.at));

        if (!cancelled) setItems(merged);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [wsId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="inbox-all-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="p-12 text-center" data-testid="inbox-all-empty">
        <Inbox className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
        <h3 className="text-lg font-semibold mb-1">All quiet on the client front</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          When clients reply to an invoice email or message you from their portal, conversations land here in one stream.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-2" data-testid="inbox-all-list">
      {items.map((it) => (
        <button
          key={it.id}
          onClick={() => navigate(it.link)}
          className="w-full text-left p-4 rounded-lg border bg-card hover:border-violet-300 dark:hover:border-violet-700 transition-colors group"
          data-testid={`inbox-item-${it.id}`}
        >
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 p-2 rounded-md ${it.source === 'email' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300' : 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300'}`}>
              {it.source === 'email' ? <Mail className="w-4 h-4" /> : <MessageCircle className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm truncate">{it.from}</span>
                <Badge
                  variant="outline"
                  className={`text-[10px] py-0 h-4 ${it.source === 'email' ? 'border-blue-200 text-blue-700 dark:border-blue-900 dark:text-blue-300' : 'border-violet-200 text-violet-700 dark:border-violet-900 dark:text-violet-300'}`}
                >
                  {it.source === 'email' ? 'Email' : 'Portal'}
                </Badge>
                {it.unread && <span className="w-2 h-2 rounded-full bg-violet-500" data-testid="inbox-unread-dot" />}
                <span className="text-xs text-muted-foreground ml-auto whitespace-nowrap">{formatRelative(it.at)}</span>
              </div>
              <div className="text-sm font-medium text-foreground mb-0.5 truncate">{it.subject}</div>
              <div className="text-xs text-muted-foreground line-clamp-2">{it.preview || '(no preview)'}</div>
            </div>
            <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
          </div>
        </button>
      ))}
    </div>
  );
}

export default function InboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Map legacy paths to the corresponding tab on first paint.
  const defaultTab = useMemo(() => {
    if (searchParams.get('tab')) return searchParams.get('tab');
    if (location.pathname.startsWith('/client-replies')) return 'email';
    if (location.pathname.startsWith('/messages')) return 'portal';
    return 'all';
  }, [location.pathname, searchParams]);

  const [tab, setTab] = useState(defaultTab);

  const onTabChange = (next) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('tab'); else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Inbox className="w-6 h-6 text-violet-600" />
            Client Inbox
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every client conversation in one place — email replies to your invoices &amp; bills and direct messages from the Client Portal.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={onTabChange} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="all" data-testid="inbox-tab-all">
            <Inbox className="w-3.5 h-3.5 mr-1.5" /> All
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="inbox-tab-email">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
          </TabsTrigger>
          <TabsTrigger value="portal" data-testid="inbox-tab-portal">
            <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Portal
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <AllStream />
        </TabsContent>

        <TabsContent value="email" className="space-y-4 -mt-2">
          {/* Existing email-replies surface, wrapped */}
          <ClientReplies embedded />
        </TabsContent>

        <TabsContent value="portal" className="space-y-4 -mt-2">
          {/* Existing portal messages surface, wrapped */}
          <MessagesPage embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
